import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { useCustomPlanInfo } from "@/hooks/useCustomPlanNavigation";
import { SubscriptionFeatures } from "@/lib/types";
import { UpgradePrompt } from "./UpgradePrompt";
import { Shell } from "@/components/layout/Shell";
import { Skeleton } from "@/components/ui/skeleton";

interface FeatureRouteProps {
  feature: keyof SubscriptionFeatures | string;
  children: ReactNode;
  redirectTo?: string;
  featureName?: string;
}

export function FeatureRoute({ 
  feature, 
  children, 
  redirectTo,
  featureName 
}: FeatureRouteProps) {
  const { hasFeature, isLoading: isLoadingFeatures } = useFeatureFlags();
  const { data: customPlanInfo, isLoading: isLoadingCustomPlan } = useCustomPlanInfo();

  const isLoading = isLoadingFeatures || isLoadingCustomPlan;

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </Shell>
    );
  }

  // For custom plans, check if the feature is in their selected features
  const isCustomPlan = customPlanInfo?.isCustomPlan || false;
  const customFeatureKeys = customPlanInfo?.featureKeys || [];
  
  let hasAccess = false;
  
  if (isCustomPlan) {
    // Custom plan: check if feature is in their enabled feature keys
    hasAccess = customFeatureKeys.includes(feature as string);
  } else {
    // Regular plan: use the standard hasFeature check
    hasAccess = hasFeature(feature as keyof SubscriptionFeatures);
  }

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    return (
      <Shell>
        <UpgradePrompt 
          feature={featureName || formatFeatureName(feature)} 
          variant="card"
          className="max-w-2xl mx-auto mt-20"
        />
      </Shell>
    );
  }

  return <>{children}</>;
}

function formatFeatureName(feature: keyof SubscriptionFeatures | string): string {
  const displayNames: Record<string, string> = {
    erp_integration: "ERP Integration",
    custom_fields: "Custom Fields",
    calendar_view: "Calendar View",
    contract_notes: "Contract Notes",
    duplicate_detection: "Duplicate Detection",
    api_read: "API Read Access",
    api_write: "API Write Access",
    advanced_dashboard: "Advanced Dashboard",
    document_extraction: "Document Extraction",
    sales_analytics: "Sales Analytics",
    cost_optimization: "Cost Optimization"
  };
  
  return displayNames[feature] || feature.replace(/_/g, ' ');
}
